import React, { useContext } from 'react';
import slugify from 'slugify';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { IconLegend } from 'src/pages/CatalogPage/components/FilterMenu/IconLegend/IconLegend';
import { ManuFilterModal } from 'src/pages/CatalogPage/components/FilterMenu/ManuFilterModal/ManuFilterModal';
import { CaliberFilterModal } from 'src/pages/CatalogPage/components/FilterMenu/CaliberFilterModal/CaliberFilterModal';
import { FilterOption } from 'src/pages/CatalogPage/components/FilterMenu/FilterMenuOption/FilterMenuOption';
import { ClearFilters } from 'src/pages/CatalogPage/components/FilterMenu/ClearFilters/ClearFilters';
import { FilterSection } from 'src/pages/CatalogPage/components/FilterMenu/FilterSection/FilterSection';
import {
  FilterMenuContainer,
  FilterBlock,
  SubcategoryFilterBlock,
  CollectionsButton,
} from 'src/pages/CatalogPage/components/FilterMenu/FilterMenu.style';
import { IFacets, ISearchInput } from 'src/pages/CatalogPage/CatalogPage.i';
import { IAuctionMappings, TAuctionCollections } from 'src/pages/CatalogPage/Catalog.i';
import { TextButton } from '@riac/design-system';

import { useOverlayTriggerState } from '@react-stately/overlays';

export interface IFilterMenuProps {
  facets: IFacets;
  updateFilter(slug: string, field: keyof IAuctionMappings): void;
  mappings: IAuctionMappings;
  collections: TAuctionCollections;
  searchInput: ISearchInput;
  searchBusy?: boolean;
}

export const FilterMenu = ({
  facets,
  updateFilter,
  mappings,
  collections,
  searchInput,
}: IFilterMenuProps) => {
  const calModalOverlayState = useOverlayTriggerState({});
  const manuModalOverlayState = useOverlayTriggerState({});

  const { auctionId, gotoAddress, auctionTypeId } = useContext(CatalogContext);

  function changeFilter(slug: string, field: keyof IAuctionMappings) {
    updateFilter(slug, field);
    if(field !== 'caliber' && field !== 'session') {
      // storeCatalog.searchBarEl.scrollIntoView();
    }
  }

  const hloFilters = mappings.hlo;
  const amCodeFilters = mappings.amcode;

  return (
    <FilterMenuContainer>
      <FilterBlock isLoading={false}>
        <ClearFilters searchInput={searchInput} />
      </FilterBlock>
      {facets.lotNumber && (
        <FilterSection heading='Auction Day' isLoading={!facets.lotNumber}>
          {() => (
            <ul>
              {mappings.session.map((session) => {
                return (
                  <FilterOption
                    field='session'
                    key={session.value}
                    option={session}
                    setFilter={changeFilter}
                    count={facets.lotNumber[`[${session.startingLot},${session.endingLot}]`]}
                    checked={
                      searchInput.session == session.value
                      || (session.name === 'All Days' && searchInput.session === '')
                    }
                  />
                );
              })}
            </ul>
          )}
        </FilterSection>
      )}
      {collections.length > 0 && (
        <FilterSection heading='Collections' isLoading={false}>
          {() => (
            <ul>
              {collections.map((v) => {
                return (
                  <li key={v.Collection}>
                    <CollectionsButton onPress={() => gotoAddress(`/catalog/${auctionId}/?savedSearch=${slugify(v.Collection)}`)}>
                      {v.Collection}
                    </CollectionsButton>
                  </li>
                );
              })}
            </ul>
          )}
        </FilterSection>
      )}
      {facets.hasBids && auctionTypeId === TIMED_AUCTION_TYPE_ID && <FilterSection isLoading={false} heading={'RIAC On Time'}>
        {() => (
          <ul>
            <FilterOption
              option={{ name: 'All', slug: '', value: 'All' }}
              checked={searchInput.hasBids !== 'false'}
              setFilter={changeFilter}
              field={'hasBids'}
              count={0} />
            <FilterOption
              option={{ name: 'No Bids', slug: 'hasBids', value: 'false' }}
              checked={searchInput.hasBids === 'false'}
              setFilter={changeFilter}
              field={'hasBids'}
              count={facets.hasBids.false || 0} />
          </ul>
        )}
      </FilterSection>
      }
      <FilterSection heading='Product Type' isLoading={!facets.hlo || !facets.gunType}>
        {() => (
          <ul>
            {hloFilters.map((hlo) => {
              const count
                = facets.hlo[hlo.value]
                || facets.gunType[hlo.value]
                || (hlo.value == 'All' && 1)
                || 0;
              return (
                <FilterOption
                  field='hlo'
                  key={hlo.value}
                  option={hlo}
                  setFilter={changeFilter}
                  count={count}
                  checked={
                    searchInput.hlo === hlo.value || (hlo.name === 'All' && searchInput.hlo === '')
                  }
                />
              );
            })}
          </ul>
        )}
      </FilterSection>
      <FilterSection heading='Category' isLoading={!facets.amCodeLot}>
        {() => (
          <ul>
            {amCodeFilters.map((amcode) => {
              let amCodeLotA = 0;
              let amCodeLotM = 0;
              let amCodeLotI = 0;
              let amCodeLotC = 0;
              if(facets) {
                for(let i = 0; i < Object.keys(facets.amCodeLot).length; i++) {
                  const facet = Object.keys(facets.amCodeLot)[i];
                  if(facet.indexOf('A') !== -1) {
                    amCodeLotA += facets.amCodeLot[facet];
                  }
                  if(facet.indexOf('M') !== -1) {
                    amCodeLotM += facets.amCodeLot[facet];
                  }
                  if(facet.indexOf('I') !== -1) {
                    amCodeLotI += facets.amCodeLot[facet];
                  }
                  if(facet.indexOf('C') !== -1) {
                    amCodeLotC += facets.amCodeLot[facet];
                  }
                }
              }
              const count
                = (amcode.name == 'Antique Firearms' && amCodeLotA)
                || (amcode.name == 'Antique Lots' && facets.amCodeLot['A'])
                || (amcode.name == 'Curio & Relic' && amCodeLotC)
                || (amcode.name == 'Class III' && amCodeLotI)
                || (amcode.name == 'Modern Firearms' && amCodeLotM)
                || (amcode.name == 'All' && 123)
                || 0;

              return (
                <FilterOption
                  field='amcode'
                  key={amcode.value}
                  option={amcode}
                  setFilter={changeFilter}
                  count={count}
                  checked={
                    searchInput.amcode === amcode.value
                    || (amcode.name === 'All' && searchInput.amcode === '')
                  }
                />
              );
            })}
          </ul>
        )}
      </FilterSection>
      <FilterSection heading='Manufacturer' isLoading={!facets.manufacturerName}>
        {() => (
          <ul>
            <FilterOption
              field='manu'
              option={{ slug: '', value: 'All', name: 'All' }}
              setFilter={changeFilter}
              checked={searchInput.manu === ''}
            />
            {searchInput.manu && (
              <FilterOption
                field='manu'
                option={{ slug: '', value: searchInput.manu, name: searchInput.manu }}
                count={facets.manufacturerName[searchInput.manu.trim()]}
                checked={true}
              />
            )}
            {facets.manufacturerName
              && Object.keys(facets.manufacturerName)
                .filter((facet) => facet.trim() !== searchInput.manu.trim())
                .sort((a, b) => {
                  if(a === 'None') {
                    return 1;
                  }
                  if(b === 'None') {
                    return -1;
                  }
                  return facets.manufacturerName[b] - facets.manufacturerName[a];
                })
                .slice(0, 10)
                .map((manu) => {
                  const manufacturer = mappings.manu.find(
                    (mapping) => mapping.value.trim() === manu.trim()
                  );
                  if(!manufacturer) {
                    return null;
                  }
                  const count = facets.manufacturerName[manufacturer.value.trim()] || 0;
                  return (
                    <FilterOption
                      field='manu'
                      key={manufacturer.value}
                      option={manufacturer}
                      setFilter={changeFilter}
                      count={count}
                      checked={searchInput.manu === manufacturer.value}
                    />
                  );
                })}
            <li key='seemoremanu'>
              <TextButton onPress={() => manuModalOverlayState.open()}>
                See More...
              </TextButton>
            </li>
          </ul>
        )}
      </FilterSection>
      <div>
        <FilterSection heading='Caliber' isLoading={!facets.guageNum || !facets.guageCategory}>
          {() => (
            <ul>
              <FilterOption
                field='caliber'
                option={{ slug: '', value: 'All', name: 'All' }}
                setFilter={changeFilter}
                checked={searchInput.caliber === ''}
              />
              {facets.guageNum
                && Object.keys(facets.guageNum)
                  .filter((facet) => facets.guageNum[facet] > 0)
                  .sort((a, b) => {
                    return facets.guageNum[b] - facets.guageNum[a];
                  })
                  .slice(0, 5)
                  .map((cal) => {
                    const caliber = mappings.caliber.filter(
                      (mapping) => mapping.value.trim() === cal.trim()
                    )[0];
                    if(!caliber) {
                      return null;
                    }
                    const count = facets.guageNum[caliber.value.trim()] || 0;
                    let isVisible = 'display: none;';
                    if(caliber.value === searchInput.caliber) {
                      isVisible = 'font-weight: bold;';
                    } else if(searchInput.caliber === '') {
                      isVisible = '';
                    }
                    return (
                      <FilterOption
                        field='caliber'
                        key={caliber.value}
                        option={caliber}
                        setFilter={changeFilter}
                        count={count}
                        checked={searchInput.caliber === caliber.value}
                        addedStyle={isVisible}
                      />
                    );
                  })}
              {searchInput.caliber ? (
                <SubcategoryFilterBlock>
                  <FilterOption
                    field='subcaliber'
                    option={{ slug: '', value: 'All', name: `All ${searchInput.caliber}` }}
                    setFilter={changeFilter}
                    checked={searchInput.subcaliber === ''}
                  />
                  {searchInput.subcaliber && (
                    <FilterOption
                      field='subcaliber'
                      option={{
                        slug: '',
                        value: searchInput.subcaliber,
                        name: searchInput.subcaliber,
                      }}
                      count={facets.guageCategory[searchInput.subcaliber]}
                      checked={true}
                    />
                  )}
                  {facets.guageCategory
                    && Object.keys(facets.guageCategory)
                      .filter((facet) => facet !== searchInput.subcaliber)
                      .filter((facet) => facets.guageCategory[facet] > 0)
                      .sort((a, b) => {
                        return facets.guageCategory[b] - facets.guageCategory[a];
                      })
                      .slice(0, 5)
                      .map((subcal) => {
                        const subcaliber = mappings.subcaliber.filter(
                          (mapping) => mapping.value.trim() === subcal.trim()
                        )[0];
                        if(!subcal) {
                          return null;
                        }
                        const count = facets.guageCategory[subcaliber.value.trim()] || 0;
                        return (
                          <FilterOption
                            field='subcaliber'
                            key={subcaliber.value}
                            option={subcaliber}
                            setFilter={changeFilter}
                            count={count}
                            checked={searchInput.subcaliber === subcaliber.value}
                          />
                        );
                      })}
                </SubcategoryFilterBlock>
              ) : null}
              <li key='seemoremanu'>
                <TextButton onPress={() => calModalOverlayState.open() }>
                  See More...
                </TextButton>
              </li>
            </ul>
          )}
        </FilterSection>
      </div>
      <FilterBlock isLoading={false}>
        <ClearFilters searchInput={searchInput} />
      </FilterBlock>
      <IconLegend />
      {manuModalOverlayState.isOpen && (
        <ManuFilterModal
          changeFilter={changeFilter}
          close={() => manuModalOverlayState.close()}
          manuMappings={mappings.manu}
          manu={searchInput.manu}
          facets={facets.manufacturerName}
        />
      )}
      {calModalOverlayState.isOpen && (
        <CaliberFilterModal
          changeFilter={changeFilter}
          close={() => calModalOverlayState.close()}
          subcalMappings={mappings.subcaliber}
          cal={searchInput.caliber}
          subcal={searchInput.subcaliber}
          facets={facets.guageCategory}
        />
      )}
    </FilterMenuContainer>
  );
};
